import './index.scss';
import React, {FocusEvent, FunctionComponent, KeyboardEvent, SetStateAction, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {BizFormForm, BizFormQuestion, BizFormQuestionOption, BizFormQuestionTypeIds, BizFormQuestionTypeIdTypeMap} from "../../index";
import {ApiResponseDs, UpdateFormRequest, UpdateOptionRequest, UpdateQuestionRequest} from "../../core/services/api";
import {BizFormApiProvider, useBizFormApi, WithBizFormApiProviderOptions} from "../context/biz-form-api-context";
import axios, {CancelToken} from "axios";
import useIsMounted from "../hooks/use-is-mounted";
import {Box, Button, Card, Checkbox, CircularProgress, Fade, FormControlLabel, IconButton, MenuItem, TextField, Tooltip, Typography, Unstable_Grid2 as Grid} from "@mui/material";
import Utils from "../../core/services/utils";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import Form, {MuiRFF, Yup} from "../../../../ui/components/form";
import classnames from "classnames";
import {ContentCopyRounded, DeleteOutlined, DragHandleRounded, EditOutlined} from "@mui/icons-material";
import SaveOrRollback from "../../../../ui/components/app-specific/save-or-rollback";
import {ReactSortable, ReactSortableProps} from "react-sortablejs";
// @ts-ignore
import {ReactComponent as CheckboxIcon} from "../../../../assets/images/check-box/checkbox.svg";
// @ts-ignore
import {ReactComponent as ActiveCheckboxIcon} from "../../../../assets/images/check-box/checkbox-active.svg";
// @ts-ignore
import {ReactComponent as Icon} from "../../../../assets/images/part-support-request/checkbox.svg";
// @ts-ignore
import {ReactComponent as IconCheckbox} from "../../../../assets/images/part-support-request/checkbox-active.svg";
import LoadingLottieAnimation from "../../../../ui/components/app-specific/lottie-animations/loading";
import {Checkbox as CheckboxInternal} from "../../../../ui/components/base/checkbox/checkbox";

export interface BizFormEditFormProps extends WithBizFormApiProviderOptions {
    formId: BizFormForm['id'];
    removeConfirmationDialog: (open: boolean, data: any) => void;
}

interface IOption extends BizFormQuestionOption {
    inEditMode: boolean;
    editing: boolean;
    removing: boolean;
}

interface Question extends BizFormQuestion {
    inEditMode: boolean;
    editing: boolean;
    removing: boolean;
    options: IOption[];
}

interface IForm extends BizFormForm {
    questions: Question[];
}


const BizFormEditFormInner: FunctionComponent<BizFormEditFormProps> = (props) => {
    const api = useBizFormApi();
    const {removeConfirmationDialog} = props;

    const [loading, setLoading] = useState(true);
    const [updatingTitle, setUpdatingTitle] = useState(false);
    const [updatingDescription, setUpdatingDescription] = useState(false);
    const [creatingQuestion, setCreatingQuestion] = useState(false);
    const [updatingIsPublic, setUpdatingIsPublic] = useState(false);
    const [form, setForm] = useState<IForm>();
    const [error, setError] = useState<ApiResponseDs>();
    const [reordering, setReordering] = useState(false);
    const isMounted = useIsMounted();

    const savedForm = useRef<IForm>();

    /**
     * Fetches the form by the form token.
     */
    const fetchForm = useCallback(async (cancelToken?: CancelToken) => {
        setLoading(true);
        const response = await api.getForm({id: props.formId}, cancelToken);
        if (!isMounted())
            return;
        if (!response?.aborted) {
            setLoading(false);
        }
        if (response?.resultFlag && !!response.data) {
            const questions = response.data.questions.map(q => {
                const options = q.options.map(o => ({
                    ...o,
                    inEditMode: false,
                    editing: false,
                    removing: false,
                }));
                options.sort((a, b) => a.orderIndex - b.orderIndex);
                return ({
                    ...q,
                    inEditMode: false,
                    editing: false,
                    removing: false,
                    options: options,
                })
            });
            questions.sort((a, b) => a.orderIndex - b.orderIndex);
            setForm({
                ...response.data,
                questions,
            });
            savedForm.current = {
                ...response.data,
                questions,
            };
        } else {
            setError(response);
        }
    }, [api, isMounted, props.formId])

    /**
     * As soon as the component mounts:
     * - fetches the form from the server.
     */
    useEffect(() => {
        if (!props.formId)
            return;
        const cancelTokenSource = axios.CancelToken.source();
        fetchForm(cancelTokenSource.token).then();
        return () => cancelTokenSource.cancel();
    }, [fetchForm, props.formId])

    /**
     * Updates the form with the given changes.
     */
    const updateForm = useCallback(async (changes: Partial<IForm>) => {
        const forApi: UpdateFormRequest = {
            id: form?.id!,
            title: '',
            updatedProperties: [],
        }
        if ('title' in changes) {
            forApi.title = changes.title!;
            forApi.updatedProperties.push('Title');
        }
        if ('description' in changes) {
            forApi.description = changes.description;
            forApi.updatedProperties.push('Description');
        }
        if ('isPublic' in changes) {
            forApi.isPublic = changes.isPublic;
            forApi.updatedProperties.push('IsPublic');
        }
        const response = await api.updateForm(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setForm(prevState => {
                const newForm = {...prevState!, ...changes,}
                savedForm.current = newForm;
                return newForm;
            });
        }
    }, [api, form?.id, isMounted]);

    /**
     * Saves the title of the form.
     */
    const saveTitle = useCallback(async (e: FocusEvent<HTMLInputElement>) => {
        if (savedForm.current?.title === e.target.value)
            return;
        if (e.target.value.length > 50) {
            e.preventDefault();
            return;
        }
        setUpdatingTitle(true);
        await updateForm({title: e.target.value});
        if (!isMounted())
            return;
        setUpdatingTitle(false);
    }, [isMounted, updateForm])

    /**
     * Saves the description of the form.
     */
    const saveDescription = useCallback(async (e: FocusEvent<HTMLInputElement>) => {
        if (form?.description === e.target.value)
            return;
        setUpdatingDescription(true);
        await updateForm({description: e.target.value});
        if (!isMounted())
            return;
        setUpdatingDescription(false);
    }, [isMounted, form?.description, updateForm])

    /**
     * Saves the isPublic flag of the form.
     */
    const saveIsPublic = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (form?.isPublic === e.target.checked)
            return;
        setUpdatingIsPublic(true);
        await updateForm({isPublic: e.target.checked});
        if (!isMounted())
            return;
        setUpdatingIsPublic(false);
    }, [isMounted, form?.isPublic, updateForm])

    /**
     * Creates a new question in the form.
     */
    const createQuestion = useCallback(async (questionId?: number) => {
        let duplicate: Omit<BizFormQuestion, 'id' | 'orderIndex'> | undefined;
        if (questionId) {
            duplicate = form?.questions.find(q => q.id === questionId)
        }
        if (!duplicate) {
            duplicate = {
                content: '',
                isRequired: false,
                questionTypeId: BizFormQuestionTypeIds.shortAnswer,
                options: [],
            };
        }
        setCreatingQuestion(true);
        const response = await api.createQuestion({
            formId: props.formId,
            ...duplicate!,
            typeId: duplicate!.questionTypeId,
        });
        if (!isMounted())
            return;
        setCreatingQuestion(false);
        if (response?.resultFlag && !!response.data) {
            setForm(prevState => ({
                ...prevState!,
                questions: [
                    ...prevState!.questions,
                    {
                        ...response.data!,
                        inEditMode: true,
                        editing: false,
                        removing: false,
                        options: []
                    },
                ]
            }));
            if (questionId) {
                // create options here
                for (const option of duplicate!.options) {
                    const optionResponse = await api.createOption({
                        questionId: response.data.id,
                        content: option.content ?? "",
                    });
                    if (!isMounted())
                        return;
                    if (optionResponse?.resultFlag && !!optionResponse.data) {
                        setForm(prevState => ({
                            ...prevState!,
                            questions: prevState!.questions.map(q => q.id === response.data!.id
                                ? {
                                    ...q,
                                    options: [
                                        ...q.options,
                                        {
                                            ...optionResponse.data!,
                                            inEditMode: false,
                                            editing: false,
                                            removing: false,
                                        }
                                    ],
                                }
                                : q
                            ),
                        }));
                    }
                }
            }
        }
    }, [api, form?.questions, isMounted, props.formId])

    /**
     * Updates the question with the given changes.
     */
    const updateQuestion = useCallback<BizFormEditFormQuestionCardProps['update']>(async (questionId, changes, internal) => {
        if (internal) {
            setForm(prevState => ({
                ...prevState!,
                questions: prevState!.questions.map(q => q.id === questionId ? typeof changes === 'function' ? {...q, ...changes(q)} : {...q, ...changes} : q),
            }));
        } else {
            const forApi: UpdateQuestionRequest = {
                id: questionId,
                content: "",
                isRequired: false,
                typeId: BizFormQuestionTypeIds.shortAnswer,
                updatedProperties: [],
            }
            if ('content' in changes) {
                forApi.content = changes.content!;
                forApi.updatedProperties.push('Content');
            }
            if ('description' in changes) {
                forApi.description = changes.description;
                forApi.updatedProperties.push('Description');
            }
            if ('isRequired' in changes) {
                forApi.isRequired = changes.isRequired!;
                forApi.updatedProperties.push('IsRequired');
            }
            if ('questionTypeId' in changes) {
                forApi.typeId = changes.questionTypeId! as any;
                forApi.updatedProperties.push('TypeId');
            }
            setForm(prevState => ({
                ...prevState!,
                questions: prevState!.questions.map(q => q.id === questionId ? {...q, editing: true} : q),
            }));
            const response = await api.updateQuestion(forApi);
            if (!isMounted())
                return;
            if (response?.resultFlag) {
                setForm(prevState => ({
                    ...prevState!,
                    questions: prevState!.questions.map(q => q.id === questionId ? {
                        ...q, ...changes,
                        inEditMode: false
                    } : q),
                }));
            }
            setForm(prevState => ({
                ...prevState!,
                questions: prevState!.questions.map(q => q.id === questionId ? {...q, editing: false} : q),
            }));
        }
    }, [api, isMounted])

    /**
     * Deletes the question with the given id.
     */
    const deleteQuestion = useCallback<BizFormEditFormQuestionCardProps['delete']>(async (questionId) => {
        return await new Promise((resolve) => {
            removeConfirmationDialog(true, {
                open: true,
                onClose: async (confirmed) => {
                    if (!confirmed) {
                        resolve();
                        return true;
                    }
                    setForm(prevState => ({
                        ...prevState!,
                        questions: prevState!.questions.map(q => q.id === questionId ? {...q, removing: true} : q),
                    }));
                    const response = await api.deleteQuestion({questionId: questionId});
                    if (!isMounted()) {
                        resolve();
                        return true;
                    }
                    if (response?.resultFlag) {
                        setForm(prevState => ({
                            ...prevState!,
                            questions: prevState!.questions.filter(q => q.id !== questionId),
                        }));
                    } else {
                        setForm(prevState => ({
                            ...prevState!,
                            questions: prevState!.questions.map(q => q.id === questionId ? {...q, removing: false} : q),
                        }));
                    }
                    resolve();
                    return true;
                }
            })
        })
    }, [api, isMounted, removeConfirmationDialog])

    /**
     * Changes the order of the question with the given id.
     */
    const changeQuestionOrder = useCallback<Required<ReactSortableProps<Question>>['onUpdate']>(async (e) => {
        const orderedEntries = Utils.reorderList(form?.questions!, e.oldDraggableIndex!, e.newDraggableIndex!, 'orderIndex');
        setForm(prevState => ({...prevState!, questions: orderedEntries}));
        setReordering(true);
        const response = await api.changeQuestionOrder({
            id: form?.questions[e.oldDraggableIndex!].id!,
            newIndex: e.newDraggableIndex! + 1,
            formId: form?.id!!,
        })
        if (!isMounted())
            return;
        setReordering(false);
        if (!response?.resultFlag) {
            setForm(prevState => ({...prevState!, questions: form?.questions!}));
        }
    }, [api, isMounted, form?.questions, form?.id])

    /**
     * Toggles the edit mode of the entry.
     */
    const toggleInEditMode = useCallback<BizFormEditFormQuestionCardProps['toggleInEditMode']>((questionId, inEditMode = false) => {
        setForm(prevState => ({
            ...prevState!,
            questions: prevState!.questions.map(q => q.id === questionId ? {...q, inEditMode} : q),
        }));
    }, [setForm])

    return (
        <>
            {
                !loading && !!form &&
                <Grid container spacing={2}>
                    <Grid xs>
                        <TextField
                            size={'small'}
                            label={'Title'}
                            defaultValue={form.title}
                            fullWidth
                            error={form.title?.length > 50}
                            helperText={`${form.title?.length ?? 0} / 50 characters. `.concat(form.title?.length > 50 ? "max 50 characters" : "")}
                            onChange={(e) => setForm(prevState => ({
                                ...prevState!,
                                title: e.target.value ?? "",
                            }))}
                            onBlur={saveTitle}
                            InputProps={{
                                endAdornment: updatingTitle
                                    ? <CircularProgress size={15} color={'primary'}/>
                                    : undefined
                            }}
                        />
                    </Grid>
                    <Grid xs={'auto'}>
                        <Tooltip
                            title={"Indicates whether the form is publically accessible. Public forms can be filled by anyone including your customers and their members. Private forms can only be filled by your internal operators."}
                            placement={'top'}>
                            <Box component={'span'}>
                                <FormControlLabel
                                    sx={{margin: 0}}
                                    label={<Typography variant={'body2'}>Public</Typography>}
                                    control={
                                        <Checkbox
                                            checked={form.isPublic}
                                            icon={<CheckboxIcon/>}
                                            checkedIcon={<ActiveCheckboxIcon/>}
                                            onChange={(e) => saveIsPublic(e)}
                                            disabled={updatingIsPublic}
                                        />
                                    }
                                />
                            </Box>
                        </Tooltip>
                    </Grid>
                    <Grid xs={12}>
                        <TextField
                            size={'small'}
                            label={'Description'}
                            defaultValue={form.description}
                            fullWidth
                            onBlur={saveDescription}
                            multiline
                            InputProps={{
                                endAdornment: updatingDescription
                                    ? <CircularProgress size={15} color={'primary'}/>
                                    : undefined
                            }}
                        />
                    </Grid>
                </Grid>
            }
            <Card variant={'outlined'} sx={{padding: 2}} className={'biz-form-edit-form'}>
                {
                    loading &&
                    <>
                        <div className='my-2 w-100'/>
                        <LoadingLottieAnimation className={''} width={'80%'}/>
                        <div className='my-2 w-100'/>
                    </>
                }
                {
                    !loading && !form &&
                    <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, paddingBlock: 4}}>
                        <Typography variant={'body2'} textAlign={'center'}>
                            We are so sorry but the form could not be fetched. <br/>
                            {error?.message ?? ""} <br/>
                            {
                                error?.referenceNumber
                                    ? <>` Reference no: ${error.referenceNumber}` <br/> </>
                                    : ""
                            }
                            {error?.code ? ` Error Code: ${error.code}` : ""}
                        </Typography>
                        <Button size={'medium'} variant={'outlined'} onClick={() => fetchForm()}>
                            Retry fetching the form.
                        </Button>
                    </Box>
                }
                {
                    !loading && !!form &&
                    <>
                        <Grid container spacing={2}>
                            <Grid xs={12}>
                                <Typography variant={'body2'} fontWeight={600}>
                                    Form Questions
                                </Typography>
                            </Grid>
                            <Grid xs={12}>
                                <ReactSortable
                                    scroll
                                    forceAutoScrollFallback
                                    scrollSensitivity={100}
                                    scrollSpeed={60}
                                    list={form.questions}
                                    setList={() => void 0}
                                    onUpdate={changeQuestionOrder}
                                    handle={'.handle'}
                                    ghostClass={'ghost'}
                                    animation={210}
                                    swapThreshold={0.65}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 15,
                                    }}
                                >
                                    {
                                        form.questions.map((question, index, array) => (
                                            <BizFormEditFormQuestionCard
                                                key={question.id}
                                                entry={question}
                                                update={updateQuestion}
                                                delete={deleteQuestion}
                                                sortable={!reordering && !array.some(q => q.editing || q.removing)}
                                                duplicate={createQuestion}
                                                toggleInEditMode={toggleInEditMode}
                                            />
                                        ))
                                    }
                                </ReactSortable>
                            </Grid>
                            <Grid xs={12} sx={{display: 'flex', justifyContent: 'center'}}>
                                <Button
                                    size={'medium'}
                                    variant={'outlined'}
                                    disabled={creatingQuestion}
                                    onClick={() => createQuestion()}
                                >
                                    {
                                        creatingQuestion
                                            ? "Adding New Question..."
                                            : 'Add New Question'
                                    }
                                </Button>
                            </Grid>
                        </Grid>
                    </>
                }
            </Card>
        </>
    );
}

export interface BizFormEditFormQuestionCardProps {
    entry: Question;
    update: (id: Question['id'], changes: SetStateAction<Partial<Question>>, internal: boolean) => Promise<void>;
    delete: (questionId: Question['id']) => Promise<void>;
    duplicate: (questionId?: Question['id']) => Promise<void>;
    toggleInEditMode: (questionId: Question['id'], inEditMode?: boolean) => void;
    sortable: boolean;
}

const BizFormEditFormQuestionCard: FunctionComponent<BizFormEditFormQuestionCardProps> = (props) => {
    const api = useBizFormApi();
    const {update, delete: remove, toggleInEditMode, duplicate} = props;
    const [newOption, setNewOption] = useState<IOption>();
    const [settingTrueFalseOptions, setSettingTrueFalseOptions] = useState(false);
    const [reordering, setReordering] = useState(false);
    const isMounted = useIsMounted();
    const formRef = useRef<any>(null);
    // eslint-disable-next-line
    const formId = useMemo(() => Utils.createUUID(), [props.entry.id]);

    const schema = useMemo(() => {
        return Yup.object().shape({
            content: Yup.string().trim().nullable().required(ValidateMessages.required),
            // description: Yup.string().trim().nullable(),
            typeId: Yup.number().nullable().required(),
            isRequired: Yup.boolean().nullable().required().default(false),
        })
    }, []);

    const validate = useMemo(() => MuiRFF.makeValidateSync(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const initialValues = useMemo(() => ({
        content: props.entry.content ?? "",
        // description: props.entry.description ?? '',
        typeId: props.entry.questionTypeId ?? "",
        isRequired: props.entry.isRequired ?? false,
    }), [props.entry])

    /**
     * Removes the entry by calling the appropriate callback in the props.
     */
    const removeEntry = useCallback(async () => {
        await remove(props.entry.id)
    }, [remove, props.entry.id])

    /**
     * Set the entry as in edit mode.
     */
    const setAsEditing = useCallback(() => {
        toggleInEditMode(props.entry.id, true);
    }, [toggleInEditMode, props.entry.id])

    /**
     * Rolls back the values of the form to its initial state.
     */
    const rollbackValues = useCallback((form) => {
        toggleInEditMode(props.entry.id, false);
        form.reset(initialValues);
    }, [initialValues, props.entry.id, toggleInEditMode]);


    /**
     * Updates the entry by calling the appropriate callback in the props.
     */
    const updateEntry = useCallback(async (values) => {
        if (
            props.entry.content === values.content &&
            // props.entry.description === values.description &&
            props.entry.isRequired === values.isRequired &&
            props.entry.questionTypeId === values.typeId
        ) {
            return toggleInEditMode(props.entry.id, false);
        }
        const changes: Partial<Question> & { id: Question['id'] } = {
            id: props.entry.id,
        }
        if (props.entry.content !== values.content) {
            changes.content = values.content;
        }
        // if (props.entry.description !== values.description) {
        //     changes.description = values.description;
        // }
        if (props.entry.isRequired !== values.isRequired) {
            changes.isRequired = values.isRequired;
        }
        if (props.entry.questionTypeId !== values.typeId) {
            changes.questionTypeId = values.typeId;
        }
        await update(props.entry.id, changes, false);
    }, [props.entry, update, toggleInEditMode])

    /**
     * Adds a new option to the form.
     */
    const addNewOption = useCallback(() => {
        setNewOption({
            id: Number.MAX_SAFE_INTEGER,
            content: '',
            orderIndex: props.entry.options.length,
            inEditMode: true,
            editing: false,
            removing: false,
        })
    }, [props.entry.options])

    /**
     * Creates a new option.
     */
    const createOption = useCallback<BizFormEditFormOptionCardProps['update']>(async (option, createNextRow) => {
        setNewOption(prevState => ({...prevState!, editing: true}))
        const response = await api.createOption({
            questionId: props.entry.id,
            content: option.content ?? "",
        });
        if (!isMounted())
            return;
        if (response?.resultFlag && !!response.data) {
            setNewOption(undefined);
            await update(props.entry.id, (prevState) => ({
                options: [
                    ...prevState.options ?? [],
                    {
                        ...response.data!,
                        inEditMode: false,
                        editing: false,
                        removing: false,
                    },
                ]
            }), true);
            if (createNextRow) {
                addNewOption();
            }
        } else {
            setNewOption(prevState => ({...prevState!, editing: false}))
        }
        return response?.resultFlag;
    }, [api, addNewOption, isMounted, props.entry.id, update])

    /**
     * Updates the option with the given changes.
     */
    const updateOption = useCallback<BizFormEditFormOptionCardProps['update']>(async (changes) => {
        const forApi: UpdateOptionRequest = {
            id: changes.id,
            content: "",
            updatedProperties: [],
        }
        if ('content' in changes) {
            forApi.content = changes.content!;
            forApi.updatedProperties.push('Content');
        }
        await update(props.entry.id, (prevState) => ({
            options: prevState.options!.map(o => o.id === changes.id ? {
                ...o,
                editing: true
            } : o)
        }), true)
        const response = await api.updateOption(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            await update(props.entry.id, (prevState) => ({
                options: prevState.options!.map(o => o.id === changes.id ? {
                    ...o, ...changes,
                    inEditMode: false
                } : o),
            }), true)
        }
        await update(props.entry.id, (prevState) => ({
            options: prevState.options!.map(o => o.id === changes.id ? {
                ...o,
                editing: false
            } : o)
        }), true)
        return response?.resultFlag;
    }, [api, isMounted, props.entry.id, update])

    /**
     * Deletes the option with the given id.
     */
    const deleteOption = useCallback<BizFormEditFormQuestionCardProps['delete']>(async (optionId) => {
        await update(props.entry.id, (prevState) => ({
            options: prevState.options!.map(q => q.id === optionId ? {
                ...q,
                removing: true
            } : q)
        }), true);
        const response = await api.deleteOption({optionId: optionId});
        if (!isMounted()) {
            return;
        }
        if (response?.resultFlag) {
            await update(props.entry.id, (prevState) => ({options: prevState.options!.filter(o => o.id !== optionId)}), true);
        } else {
            await update(props.entry.id, (prevState) => ({
                options: prevState.options!.map(q => q.id === optionId ? {
                    ...q,
                    removing: false
                } : q)
            }), true);
        }
    }, [api, isMounted, props.entry.id, update])

    /**
     * Changes the order of the question with the given id.
     */
    const changeOptionOrder = useCallback<Required<ReactSortableProps<IOption>>['onUpdate']>(async (e) => {
        const orderedEntries = Utils.reorderList(props.entry.options, e.oldDraggableIndex!, e.newDraggableIndex!, 'orderIndex');
        await update(props.entry.id, {options: orderedEntries}, true);
        setReordering(true);
        const response = await api.changeOptionOrder({
            id: props.entry.options[e.oldDraggableIndex!].id!,
            newIndex: e.newDraggableIndex! + 1,
            questionId: props.entry.id,
        })
        if (!isMounted())
            return;
        setReordering(false);
        if (!response?.resultFlag) {
            await update(props.entry.id, {options: props.entry.options}, true);
        }
    }, [api, isMounted, props.entry.id, props.entry.options, update])

    /**
     * Toggles the edit mode of the entry.
     */
    const toggleOptionInEditMode = useCallback<BizFormEditFormQuestionCardProps['toggleInEditMode']>((optionId, inEditMode = false) => {
        void update(props.entry.id, {
            options: props.entry.options?.map(e => e.id === optionId ? {
                ...e,
                inEditMode
            } : e) ?? [],
        }, true);
    }, [update, props.entry.id, props.entry.options])

    /**
     * Creates true and false options.
     */
    const createTrueFalseOptions = useCallback(async () => {
        setSettingTrueFalseOptions(true);
        // remove all options
        await update(props.entry.id, {options: props.entry.options.map(q => ({...q, removing: true})),}, true);
        const response = await api.deleteQuestionOptions({questionId: props.entry.id});
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            await update(props.entry.id, {options: []}, true);
            // create true and false options.
            await createOption({id: 0, content: 'True'}, false);
            await createOption({id: 0, content: 'False'}, false);
            if (!isMounted())
                return;
            setSettingTrueFalseOptions(false);
        } else {
            setSettingTrueFalseOptions(false);
            await update(props.entry.id, {
                options: props.entry.options.map(q => ({...q, removing: false})),
            }, true);
        }
    }, [api, createOption, isMounted, props.entry.id, props.entry.options, update]);

    /**
     * If the user presses enter, and the new option is enabled, then add the next option.
     */
    const onFormKeyDown = useCallback((e: KeyboardEvent<HTMLFormElement>) => {
        if (e.key === 'Enter') {
            if ((e.target as HTMLElement).closest('form')?.id !== formId) {
                e.preventDefault();
            } else {
                formRef.current?.submit();
            }
        }
    }, [formId])

    return (
        <>
            <Card variant={'outlined'}
                  sx={{padding: 2, ...(props.entry.inEditMode && {backgroundColor: 'background.tertiary'})}}>
                <Form
                    onSubmit={updateEntry}
                    validate={validate}
                    initialValues={initialValues}
                    formProps={{
                        id: formId,
                        onKeyDown: onFormKeyDown,
                    }}
                >
                    {({form, values}) => {
                        formRef.current = form;
                        return (
                            <Grid container xs={12} spacing={2}>
                                <Grid xs={'auto'}
                                      sx={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                                    <Fade in={props.sortable && !settingTrueFalseOptions}>
                                        <IconButton color={'paper.primary'} className={'handle'}>
                                            <DragHandleRounded/>
                                        </IconButton>
                                    </Fade>
                                </Grid>
                                <Grid xs paddingInline={0}>
                                    <MuiRFF.TextField
                                        fullWidth
                                        size={'small'}
                                        name={"content"}
                                        disabled={settingTrueFalseOptions}
                                        label={"Title"}
                                        required={required.content}
                                        className={classnames({'show-as-text': !props.entry.inEditMode})}
                                    />
                                </Grid>
                                <Grid xs={'auto'}>
                                    {
                                        !props.entry.inEditMode &&
                                        <Box sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center'}}>
                                            <IconButton
                                                disabled={props.entry.removing || settingTrueFalseOptions}
                                                size={'small'}
                                                onClick={() => duplicate(props.entry.id)}
                                                color={'primary'}
                                                sx={{backgroundColor: 'background.paper'}}
                                            >
                                                <ContentCopyRounded/>
                                            </IconButton>
                                            <IconButton
                                                disabled={props.entry.removing || settingTrueFalseOptions}
                                                size={'small'}
                                                onClick={setAsEditing}
                                                color={'primary'}
                                                sx={{backgroundColor: 'background.paper'}}
                                            >
                                                <EditOutlined/>
                                            </IconButton>
                                            <IconButton
                                                onClick={removeEntry}
                                                disabled={props.entry.removing || settingTrueFalseOptions}
                                                color={'error'}
                                                size={'small'}
                                                sx={{backgroundColor: 'background.paper'}}
                                            >
                                                {
                                                    props.entry.removing
                                                        ? <CircularProgress size={15} color={'primary'}/>
                                                        : <DeleteOutlined/>
                                                }
                                            </IconButton>
                                        </Box>
                                    }
                                    {
                                        props.entry.inEditMode &&
                                        <SaveOrRollback
                                            show
                                            column={false}
                                            showText={false}
                                            onSave={() => form.submit()}
                                            onRollback={() => rollbackValues(form)}
                                            loading={props.entry.editing}
                                        />
                                    }
                                </Grid>
                                <Grid xs={12} sx={{padding: 0}}/>
                                {/*<Grid xs={12}>*/}
                                {/*    <MuiRFF.TextField*/}
                                {/*        fullWidth*/}
                                {/*        disabled={settingTrueFalseOptions}*/}
                                {/*        size={'small'}*/}
                                {/*        name={"description"}*/}
                                {/*        label={"Description"}*/}
                                {/*        multiline*/}
                                {/*        required={required.description}*/}
                                {/*        className={classnames({'show-as-text': !props.entry.inEditMode})}*/}
                                {/*    />*/}
                                {/*</Grid>*/}
                                <Grid xs>
                                    <MuiRFF.TextField
                                        select
                                        fullWidth
                                        disabled={settingTrueFalseOptions}
                                        size={'small'}
                                        name={"typeId"}
                                        label={"Type"}
                                        required={required.typeId}
                                        // @ts-ignore
                                        onChange={(e) => {
                                            form.change('typeId', e.target.value);
                                            if (e.target.value === BizFormQuestionTypeIds.trueFalse) {
                                                void createTrueFalseOptions();
                                            }
                                        }}
                                        className={classnames({'show-as-text': !props.entry.inEditMode})}
                                    >
                                        {
                                            Object.entries(BizFormQuestionTypeIdTypeMap).map(([key, value]) => (
                                                <MenuItem key={key} value={Number(key)}>
                                                    {value}
                                                </MenuItem>
                                            ))
                                        }
                                    </MuiRFF.TextField>
                                </Grid>
                                <Grid xs={'auto'}>
                                    {
                                        !props.entry.inEditMode &&
                                        <Tooltip
                                            title={"Read Only"}
                                            className={'cursor-not-allowed-hover'}
                                            disableInteractive
                                        >
                                        <span>
                                            {/*@ts-ignore*/}
                                            <CheckboxInternal
                                                className={'cursor-not-allowed-hover'}
                                                labelClassName={'cursor-not-allowed-hover'}
                                                value={true}
                                                label={'Required to fill'}
                                                checked={values.isRequired ?? false}
                                                onChange={() => false}
                                                icon={<Icon/>}
                                                checkedIcon={<IconCheckbox/>}
                                            />
                                        </span>
                                        </Tooltip>
                                    }
                                    <MuiRFF.Checkboxes
                                        color={'primary'}
                                        icon={<CheckboxIcon/>}
                                        checkedIcon={<ActiveCheckboxIcon/>}
                                        data={{value: values.isRequired, label: 'Required to fill'}}
                                        name={'isRequired'}
                                        readOnly={!props.entry.inEditMode}
                                        disabled={settingTrueFalseOptions}
                                        required={required.isRequired}
                                        sx={{marginBlockEnd: 0, marginInlineEnd: 0,}}
                                        formControlProps={{
                                            sx: {...(!props.entry.inEditMode ? {display: 'none'} : {})},
                                        }}
                                        formControlLabelProps={{
                                            className: classnames({'show-as-text': !props.entry.inEditMode})
                                        }}
                                    />
                                </Grid>
                                <Grid
                                    xs={12}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1,
                                        ...(!Utils.showQuestionOptions(values.typeId) && {
                                            height: 0,
                                            padding: 0,
                                            overflow: 'hidden',
                                            visibility: 'hidden',
                                            opacity: '0%',
                                        }),
                                    }}
                                >
                                    <Typography variant={'caption'} fontWeight={600}>
                                        Options
                                    </Typography>
                                    <ReactSortable
                                        list={props.entry.options}
                                        setList={() => void 0}
                                        onUpdate={changeOptionOrder}
                                        handle={'.handle'}
                                        ghostClass={'ghost'}
                                        animation={210}
                                        swapThreshold={0.65}
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 15,
                                        }}
                                    >
                                        {
                                            props.entry.options.map((option, index, array) => (
                                                <BizFormEditFormOptionCard
                                                    key={option.id}
                                                    entry={option}
                                                    update={updateOption}
                                                    delete={deleteOption}
                                                    sortable={!reordering && !array.some(q => q.editing || q.removing)}
                                                    toggleInEditMode={toggleOptionInEditMode}
                                                />
                                            ))
                                        }
                                    </ReactSortable>
                                    {
                                        // props.sortable && !props.entry.options.some(q => q.editing || q.removing) &&
                                        <>
                                            {
                                                !!newOption &&
                                                <BizFormEditFormOptionCard
                                                    newOption
                                                    entry={newOption}
                                                    update={createOption}
                                                    delete={() => Promise.resolve(void 0)}
                                                    removable={false}
                                                    sortable={false}
                                                    toggleInEditMode={() => void 0}
                                                    rollback={() => setNewOption(undefined)}
                                                />
                                            }
                                            {
                                                !newOption &&
                                                <Button
                                                    size={'medium'}
                                                    variant={'outlined'}
                                                    onClick={addNewOption}
                                                >
                                                    Add New Option
                                                </Button>
                                            }
                                        </>
                                    }
                                </Grid>
                            </Grid>
                        )
                    }}
                </Form>
            </Card>
        </>
    );
}

export interface BizFormEditFormOptionCardProps {
    entry: IOption;
    update: (changes: Partial<IOption> & { id: IOption['id'] }, createNextRow: boolean) => Promise<void | boolean>;
    delete: (optionId: IOption['id']) => Promise<void>;
    sortable: boolean;
    removable?: boolean;
    rollback?: () => void;
    toggleInEditMode: (optionId: IOption['id'], inEditMode?: boolean) => void;
    newOption?: boolean;
}

const BizFormEditFormOptionCard: FunctionComponent<BizFormEditFormOptionCardProps> = (props) => {
    const {update, delete: remove, toggleInEditMode, rollback} = props;
    const id = useMemo(() => Utils.createUUID(), []);
    const addNextOption = useRef(false);
    const formRef = useRef<any>(null);
    const inputRef = useRef<any>(null);

    const schema = useMemo(() => {
        return Yup.object().shape({
            content: Yup.string().trim().nullable().required(ValidateMessages.required),
        })
    }, []);

    const validate = useMemo(() => MuiRFF.makeValidateSync(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const initialValues = useMemo(() => ({
        content: props.entry.content ?? "",
    }), [props.entry])

    /**
     * Removes the entry by calling the appropriate callback in the props.
     */
    const removeEntry = useCallback(async () => {
        await remove(props.entry.id)
    }, [remove, props.entry.id])

    /**
     * Set the entry as in edit mode.
     */
    const setAsEditing = useCallback(() => {
        toggleInEditMode(props.entry.id, true);
    }, [toggleInEditMode, props.entry.id])

    /**
     * Rolls back the values of the form to its initial state.
     */
    const rollbackValues = useCallback((form) => {
        toggleInEditMode(props.entry.id, false);
        form.reset(initialValues);
        rollback?.();
    }, [initialValues, props.entry.id, toggleInEditMode, rollback]);

    /**
     * Updates the entry by calling the appropriate callback in the props.
     */
    const updateEntry = useCallback(async (values: any, form: any) => {
        if (props.entry.content === values.content) {
            return toggleInEditMode(props.entry.id, false);
        }
        const changes: Partial<IOption> & { id: IOption['id'] } = {
            id: props.entry.id,
        }
        if (props.entry.content !== values.content) {
            changes.content = values.content;
        }
        const createNextRow = addNextOption.current;
        await update(changes, createNextRow);
        addNextOption.current = false;
        if (createNextRow) {
            form.reset(initialValues);
            form.resetFieldState('content');
            setTimeout(() => {
                inputRef.current?.focus();
            }, 300)

        }
    }, [props.entry?.content, props.entry?.id, update, toggleInEditMode, initialValues])

    /**
     * If the user presses enter, and the new option is enabled, then add the next option.
     */
    const onFormKeyDown = useCallback((e: KeyboardEvent<HTMLFormElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if ((e.target as HTMLElement).closest(`#${id}`)) {
                if (props.newOption) {
                    addNextOption.current = true;
                }
                formRef.current?.submit()
            }
        }
    }, [props.newOption, id])

    return (
        <>
            <Card variant={'outlined'} id={id}>
                <Form
                    onSubmit={updateEntry}
                    validate={validate}
                    initialValues={initialValues}
                    formProps={{
                        noValidate: true,
                        action: undefined,
                        onKeyDownCapture: onFormKeyDown
                    }}
                >
                    {({form}) => {
                        formRef.current = form;
                        return (
                            <Grid container spacing={2} marginInline={0} marginBlock={0}>
                                <Grid xs={'auto'}
                                      sx={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                                    <Fade in={props.sortable}>
                                        <IconButton color={'paper.primary'} className={'handle'}>
                                            <DragHandleRounded/>
                                        </IconButton>
                                    </Fade>
                                </Grid>
                                <Grid xs paddingInline={0}>
                                    <MuiRFF.TextField
                                        inputRef={inputRef}
                                        fullWidth
                                        size={'small'}
                                        name={"content"}
                                        label={"Title"}
                                        disabled={props.entry.removing || props.entry.editing}
                                        required={required.content}
                                        className={classnames({'show-as-text': !props.entry.inEditMode})}
                                    />
                                </Grid>
                                <Grid xs={'auto'}>
                                    {
                                        !props.entry.inEditMode &&
                                        <Box sx={{
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                            alignItems: 'center',
                                            gap: 0.5
                                        }}>
                                            <IconButton
                                                disabled={props.entry.removing}
                                                size={'small'}
                                                onClick={setAsEditing}
                                                color={'primary'}
                                                sx={{backgroundColor: 'background.paper'}}
                                            >
                                                <EditOutlined/>
                                            </IconButton>
                                            <IconButton
                                                onClick={removeEntry}
                                                disabled={props.entry.removing}
                                                color={'error'}
                                                size={'small'}
                                                sx={{backgroundColor: 'background.paper'}}
                                            >
                                                {
                                                    props.entry.removing
                                                        ? <CircularProgress size={15} color={'primary'}/>
                                                        : <DeleteOutlined/>
                                                }
                                            </IconButton>
                                        </Box>
                                    }
                                    {
                                        props.entry.inEditMode &&
                                        <SaveOrRollback
                                            show
                                            column={false}
                                            showText={false}
                                            onSave={() => {
                                                if (props.newOption) {
                                                    addNextOption.current = true;
                                                }
                                                form.submit()
                                            }}
                                            onRollback={() => rollbackValues(form)}
                                            loading={props.entry.editing}
                                        />
                                    }
                                </Grid>
                            </Grid>
                        )
                    }}
                </Form>
            </Card>
        </>
    );
}

const BizFormEditForm: FunctionComponent<BizFormEditFormProps> = (props) => {
    const {apiClient, apiBaseUrl, apiType, ...formProps} = props;
    const normalizedBaseUrl = apiBaseUrl != null && apiBaseUrl.trim() !== "" ? apiBaseUrl : undefined;

    let content = <BizFormEditFormInner {...formProps}/>;

    if (apiClient != null || normalizedBaseUrl != null) {
        content = (
            <BizFormApiProvider baseUrl={normalizedBaseUrl} client={apiClient} type={apiType}>
                {content}
            </BizFormApiProvider>
        );
    }
    return content;
};

export default BizFormEditForm;
